/**
 * 이미지 축소 → base64 (회사 로고 업로드 전용, 2026-08-12).
 *
 * 통합설정 › 회사정보(2026-08-25)를 위해 관리 콘솔의 `admin/frontend/src/lib/image-resize.ts`
 * 를 그대로 옮겨 왔다 — 내용을 바꾸지 않는다(같은 서버 상한·같은 정사각 규칙).
 * 8443 의 `lib/image-crop.ts`(cropAvatarToSquarePng)는 **잘라내기(cover)** 라 쓸 수 없다.
 * 로고는 가로형 워드마크가 잘리면 못 알아보므로 **여백을 두는(contain)** 이 함수가 맞다.
 *
 * 서버(chat-server PUT /api/v1/institution/logo)가 PNG/JPEG/WEBP · 1MB 이하만 받는다.
 * 사용자가 고른 원본이 그 상한을 넘는 경우가 흔하므로 브라우저에서 먼저 줄인다:
 *   1) 긴 변이 maxEdge 를 넘으면 비율 유지로 축소(작은 이미지는 확대하지 않는다 — 화질 손해만 남음)
 *   2) **정사각 캔버스(1:1)에 담는다** — 2026-08-12 사용자 지시("로고는 정사각형만").
 *      비율은 유지하고 남는 자리는 투명 여백(잘라내지 않음).
 *   3) PNG 로 인코딩(투명 배경 보존 — 로고는 투명 PNG 가 대부분)
 *   4) 그래도 1MB 를 넘으면 WEBP(품질 0.9 → 0.75)로 재인코딩
 * 실패(디코딩 불가 등) 시 throw — 호출부가 사용자 메시지로 변환한다.
 *
 * 캔버스 경유라 EXIF·색프로파일 등 부가 메타데이터는 제거된다(로고 용도에서는 오히려 안전).
 */

const MAX_BYTES = 1024 * 1024;

export interface ResizedImage {
  /** 'image/png' | 'image/webp' */
  contentType: string;
  /** base64 본문(데이터 URI 접두사 없음) */
  base64: string;
  bytes: number;
  width: number;
  height: number;
}

function loadImage(file: File): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => { URL.revokeObjectURL(url); resolve(img); };
    img.onerror = () => { URL.revokeObjectURL(url); reject(new Error('이미지를 읽을 수 없습니다')); };
    img.src = url;
  });
}

function toBlob(canvas: HTMLCanvasElement, type: string, quality?: number): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (b) => (b ? resolve(b) : reject(new Error('이미지 인코딩에 실패했습니다'))),
      type,
      quality,
    );
  });
}

async function blobToBase64(blob: Blob): Promise<string> {
  const buf = await blob.arrayBuffer();
  const bytes = new Uint8Array(buf);
  let bin = '';
  // 인자 개수 상한(스택 오버플로) 회피 — 8KB 청크로 나눠 문자열화.
  const CHUNK = 8192;
  for (let i = 0; i < bytes.length; i += CHUNK) {
    bin += String.fromCharCode(...bytes.subarray(i, i + CHUNK));
  }
  return btoa(bin);
}

/**
 * 로고 이미지를 긴 변 maxEdge(기본 512px) · 1MB 이하로 줄여 base64 로 반환.
 * square=true(기본)면 **정사각 캔버스**에 비율 유지로 담아 1:1 산출물을 보장한다.
 */
export async function resizeImageForUpload(file: File, maxEdge = 512, square = true): Promise<ResizedImage> {
  const img = await loadImage(file);
  const srcW = img.naturalWidth || img.width;
  const srcH = img.naturalHeight || img.height;
  if (!srcW || !srcH) throw new Error('이미지 크기를 확인할 수 없습니다');

  const scale = Math.min(1, maxEdge / Math.max(srcW, srcH));
  const drawW = Math.max(1, Math.round(srcW * scale));
  const drawH = Math.max(1, Math.round(srcH * scale));

  // 2026-08-12 (사용자 지시) — **로고는 정사각형만**. 원본 비율과 무관하게 정사각 캔버스에
  // 비율 유지로 담고(contain) 남는 자리는 투명하게 둔다. 잘라내기(cover)가 아니라 여백을 두는
  // 이유는 가로형 워드마크가 잘리면 로고 자체를 못 알아보기 때문 — 내용 손실 0 이 우선이다.
  // 결과적으로 저장물은 항상 1:1 이라 사용자 화면(72px 정사각 자리)에서 크기가 예측 가능해진다.
  const side = square ? Math.max(drawW, drawH) : drawW;
  const canvasW = square ? side : drawW;
  const canvasH = square ? side : drawH;

  const canvas = document.createElement('canvas');
  canvas.width = canvasW;
  canvas.height = canvasH;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('캔버스를 사용할 수 없습니다');
  ctx.imageSmoothingQuality = 'high';
  ctx.drawImage(img, Math.round((canvasW - drawW) / 2), Math.round((canvasH - drawH) / 2), drawW, drawH);
  const width = canvasW;
  const height = canvasH;

  // 후보 인코딩을 순서대로 시도 — 첫 1MB 이하 결과를 사용.
  const candidates: Array<{ type: string; quality?: number }> = [
    { type: 'image/png' },
    { type: 'image/webp', quality: 0.9 },
    { type: 'image/webp', quality: 0.75 },
  ];
  let last: Blob | null = null;
  for (const c of candidates) {
    const blob = await toBlob(canvas, c.type, c.quality);
    last = blob;
    // 브라우저가 요청 타입을 지원하지 않으면 다른 타입으로 떨어질 수 있다 → 실제 타입을 신뢰.
    if (blob.size <= MAX_BYTES) {
      return {
        contentType: blob.type || c.type,
        base64: await blobToBase64(blob),
        bytes: blob.size,
        width,
        height,
      };
    }
  }
  void last;
  throw new Error('이미지를 1MB 이하로 줄이지 못했습니다. 더 작은 파일을 사용해 주세요');
}
